import { washImage } from '../components/images.js';

/**
 * «Скоро выйдет» — какие серии сезона ещё не вышли и что показать вместо кадра.
 *
 * Правило одно для сайта и приложения:
 *
 *   серия не вышла, если её дата выхода (`airDate`, «ГГГГ-ММ-ДД») ПОЗЖЕ
 *   сегодняшнего дня по часам устройства. Сегодняшняя серия — вышла.
 *
 * Сравниваются календарные дни, а не мгновения: у даты выхода нет ни часа, ни
 * пояса, и «завтра» наступает в полночь устройства, а не в полночь Гринвича
 * (через `new Date('2026-10-06')` серия «вышла» бы в Москве в три часа ночи).
 *
 * Серия без даты (или с датой, которую не прочитать) считается вышедшей — кроме
 * двух случаев, когда данные сами говорят обратное:
 *
 *   — раньше неё в этом сезоне стоит серия с датой в будущем: у идущего сезона
 *     поставщик знает дату ближайшей серии, а дальнейшим её ещё не назначил;
 *   — сам сезон ещё не начался (его дата выхода в будущем).
 *
 * Серия с датой в прошлом вышла всегда, что бы ни стояло вокруг.
 */

const MONTHS = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
] as const;

interface Day { year: number; month: number; day: number }

/** Календарный день устройства: местное время, не UTC. */
function today(now: Date): Day {
  return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
}

/**
 * День из даты выхода. Принимает «ГГГГ-ММ-ДД» (и ту же дату с временем следом);
 * всё остальное — пустую строку, «скоро», 30 февраля — не читает.
 */
function parseDay(value: string | null | undefined): Day | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:$|T)/.exec(value ?? '');
  if (match === null) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  // День 0 следующего месяца — последний день этого: февраль и високосные сами.
  if (month < 1 || month > 12 || day < 1 || day > new Date(year, month, 0).getDate()) return null;
  return { year, month, day };
}

function isAfter(left: Day, right: Day): boolean {
  if (left.year !== right.year) return left.year > right.year;
  if (left.month !== right.month) return left.month > right.month;
  return left.day > right.day;
}

/** Дата выхода позже сегодняшнего дня устройства. Нет даты, кривая дата — `false`. */
export function isUpcoming(airDate: string | null | undefined, now: Date = new Date()): boolean {
  const day = parseDay(airDate);
  return day !== null && isAfter(day, today(now));
}

/**
 * Дата под надписью: «12 октября»; год — только если он не нынешний
 * («9 января 2027»). Нет даты, кривая дата — `null`, строки не будет.
 */
export function soonDateLabel(airDate: string | null | undefined, now: Date = new Date()): string | null {
  const day = parseDay(airDate);
  if (day === null) return null;
  const text = `${day.day} ${MONTHS[day.month - 1]}`;
  return day.year === now.getFullYear() ? text : `${text} ${day.year}`;
}

export interface SoonEpisode {
  /** Вторая строка заглушки; `null` — даты нет, строки нет. */
  date: string | null;
}

/**
 * Невышедшие серии сезона: номер серии → что написать на заглушке.
 * Серий, которых в ответе нет, — вышли: карточка остаётся обычной.
 */
export function upcomingEpisodes(
  episodes: ReadonlyArray<{ episodeNumber: number; airDate: string | null }>,
  seasonAirDate: string | null | undefined,
  now: Date = new Date(),
): Map<number, SoonEpisode> {
  const soon = new Map<number, SoonEpisode>();
  // Сезон не начался — серии без даты не вышли с первой же.
  let afterUpcoming = isUpcoming(seasonAirDate, now);
  const ordered = [...episodes].sort((left, right) => left.episodeNumber - right.episodeNumber);
  for (const episode of ordered) {
    if (isUpcoming(episode.airDate, now)) {
      afterUpcoming = true;
      soon.set(episode.episodeNumber, { date: soonDateLabel(episode.airDate, now) });
    } else if (afterUpcoming && parseDay(episode.airDate) === null) {
      soon.set(episode.episodeNumber, { date: null });
    }
  }
  return soon;
}

/**
 * Картинка под заглушку — одна на все невышедшие серии сезона. Копия крупная
 * (постер 500 точек, кадр серии — как пришёл, кадр сериала 780): из неё
 * считается тот же мягкий расфокус, что у фона страницы (`soft-image.ts`, вид
 * `light`), а крошечная копия дала бы только пятна. Лишней загрузки почти нет:
 * кадр серии уже стоит в соседней карточке, кадр сериала — в фоне страницы.
 * По порядку:
 *
 *   1. постер сезона;
 *   2. кадр последней ВЫШЕДШЕЙ серии этого сезона, у которой он есть;
 *   3. кадр (backdrop) сериала;
 *   4. постер сериала;
 *   5. ничего — остаётся подложка карточки без кадра.
 *
 * Кадр самой невышедшей серии не берём, даже если он пришёл: до выхода это
 * промо или случайная картинка, а заглушки сезона должны быть одинаковыми.
 * Сезонное идёт раньше общего: фон страницы — тот же кадр сериала, и заглушка
 * на нём сливалась бы со страницей.
 */
export function soonImage(input: {
  seasonPoster: string | null | undefined;
  episodes: ReadonlyArray<{ episodeNumber: number; still: { url: string } | null }>;
  upcoming: ReadonlyMap<number, SoonEpisode>;
  backdrop: string | null | undefined;
  poster: string | null | undefined;
}): string | null {
  if (input.seasonPoster) return washImage(null, input.seasonPoster);
  const aired = input.episodes
    .filter((episode) => !input.upcoming.has(episode.episodeNumber) && episode.still?.url)
    .sort((left, right) => right.episodeNumber - left.episodeNumber)[0];
  // Кадр серии — тем же адресом, что в её карточке: он уже загружен.
  return aired?.still?.url ?? washImage(input.backdrop, input.poster);
}
